import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

const ARTICLES_COLLECTION = 'articles';

/**
 * Generate a URL-friendly slug from a title
 */
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

/**
 * Get all articles, ordered by date (newest first)
 */
export const getAllArticles = async () => {
  try {
    const articlesRef = collection(db, ARTICLES_COLLECTION);
    const q = query(articlesRef, orderBy('datePublished', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const articles = [];
    querySnapshot.forEach((doc) => {
      articles.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, data: articles };
  } catch (error) {
    console.error('Error getting articles:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single article by ID
 */
export const getArticleById = async (id) => {
  try {
    const docRef = doc(db, ARTICLES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        success: true, 
        data: { id: docSnap.id, ...docSnap.data() } 
      };
    } else {
      return { success: false, error: 'Article not found' };
    }
  } catch (error) {
    console.error('Error getting article:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single article by slug
 */
export const getArticleBySlug = async (slug) => {
  try {
    const articlesRef = collection(db, ARTICLES_COLLECTION);
    const querySnapshot = await getDocs(articlesRef);
    
    let foundArticle = null;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.slug === slug) {
        foundArticle = { id: doc.id, ...data };
      }
    });
    
    if (foundArticle) {
      return { success: true, data: foundArticle };
    } else {
      return { success: false, error: 'Article not found' };
    }
  } catch (error) {
    console.error('Error getting article by slug:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a new article
 */
export const addArticle = async (articleData) => {
  try {
    // Generate slug from title
    const slug = generateSlug(articleData.title);
    
    const docRef = await addDoc(collection(db, ARTICLES_COLLECTION), {
      ...articleData,
      slug,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: docRef.id, slug };
  } catch (error) {
    console.error('Error adding article:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing article
 */
export const updateArticle = async (id, articleData) => {
  try {
    // Generate new slug if title changed
    const slug = generateSlug(articleData.title);
    
    const docRef = doc(db, ARTICLES_COLLECTION, id);
    await updateDoc(docRef, {
      ...articleData,
      slug,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, slug };
  } catch (error) {
    console.error('Error updating article:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete an article
 */
export const deleteArticle = async (id) => {
  try {
    const docRef = doc(db, ARTICLES_COLLECTION, id);
    await deleteDoc(docRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting article:', error);
    return { success: false, error: error.message };
  }
};

